import type {PropertyType, ResourceType} from 'common/report/constants';
import type {Bucket, StringBucket} from 'common/widgets/bucket-pane/types';
import {Type} from 'common/query-builder/cohorts-query-builder/models/enums/builder-enums';
import type {DateRangeAttributes} from 'common/widgets/date-range-picker/models/types';
import type {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import type {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import type {
  FilterOperator,
  FilterValue,
  ListInclusion,
  ListQuantifier,
} from 'common/widgets/property-filter-menu/models/types';
import {Event, EventItem} from 'common/query-builder/query-block/events-menu/types';
import type {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import type {FiltersOperator} from 'common/query-builder/query-entry-section/types';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {PropertyDisplayOptions} from 'apps/lexicon/src/models/property-definition';
import {RawCohort} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';

import type {MATH_TYPE, SpecialTimeUnit} from './bookmark';

export enum WindowType {
  BETWEEN = `between`,
  AFTER = `after`,
  PER_INTERVAL = `per_interval`,
}

export enum WindowLengthUnit {
  SECOND = `second`,
  MINUTE = `minute`,
  HOUR = `hour`,
  DAY = `day`,
  MONTH = `month`,
}

export const WINDOW_LENGTH_UNITS = [
  WindowLengthUnit.SECOND,
  WindowLengthUnit.MINUTE,
  WindowLengthUnit.HOUR,
  WindowLengthUnit.DAY,
  WindowLengthUnit.MONTH,
];

/**
 * Defines a behavioral computed property used in the Users page
 * and core reports for behavioral segmentation analysis
 */
export type Behavior = {
  aggregationOperator: MATH_TYPE;
  event: EventItem;
  dateRange?: DateRangeAttributes;
  property?: PropertySectionItem | null;
  /**
   * Using Segmentation[] rather than PropertyFilter[] type. Previously it was using PropertyFilter[], but this caused
   * serialization/deserialization issues when it was being pass out of mp-query-builder to Insights report and then
   * back into mp-query-builder.
   */
  filters: Segmentation[];
  filtersOperator: FiltersOperator;
  behaviorType?: string;
  startStepNumber?: number;
  endStepNumber?: number;
  windowType?: WindowType;
  windowLength?: number;
  windowLengthUnit?: WindowLengthUnit;

  multiAttribution?: MultiAttribution;
  mainDateRange?: DateRangeAttributes;

  // revenue change specific
  // any | new | upsell | churn | downgrade
  changeType?: string;

  offset?: {
    value: `$start` | number;
    unit: TimeUnit;
  };
};

export const BehavioralAttributes: Array<keyof Behavior> = [
  `behaviorType`,
  `changeType`,
  `offset`,
  `aggregationOperator`,
  `filtersOperator`,
  `event`,
  `dateRange`,
  `property`,
  `filters`,
  `multiAttribution`,
];

export enum ProfileHistoryType {
  // Use historical profile properties and treat them exactly like event properties
  EventTime = `eventTime`,

  // Use historical profile properties allowing a time range
  // e.g. "users who did any event in <time range> and have this property value"
  // TODO(jakub): can this be less confusing?
  DateRange = `dateRange`,

  // Default behavior: use latest value of profile properties
  Latest = `latest`,
}

export const ProfileHistoryTypeToDisplayName: Record<ProfileHistoryType, string> = {
  [ProfileHistoryType.EventTime]: `As of Event Time`,
  [ProfileHistoryType.Latest]: `Latest Value`,
  [ProfileHistoryType.DateRange]: `Through Time`,
};

export type BasicProfileHistoryOptions = {
  type: ProfileHistoryType.EventTime | ProfileHistoryType.Latest;
};

export type DateRangeProfileHistoryOptions = {
  type: ProfileHistoryType.DateRange;
  dateRange: DateRangeAttributes;
};

export type ProfileHistoryOptions = BasicProfileHistoryOptions | DateRangeProfileHistoryOptions;

export function isDateRangeProfileHistoryOptions(
  options: ProfileHistoryOptions,
): options is DateRangeProfileHistoryOptions {
  return options?.type === ProfileHistoryType.DateRange;
}

export type SavedCohortDefinition = {
  id: number;
  name?: string;
  negated: boolean;
};

export type InlineCohortDefinition = {
  name?: string;
  raw_cohort: RawCohort;
  negated?: boolean;
};

export type CohortDefinition = SavedCohortDefinition | InlineCohortDefinition;

/**
 * Segmentation defines a property filter or breakdown
 * this is the standard format expected by the UI on filters
 * cohorts, funnels, flows & retention APIs
 */
export type Segmentation = {
  customPropertyId?: number | string;
  customProperty?: CustomProperty;
  datasetId?: string;
  dataGroupId?: string;
  tempDataGroupId?: string;
  resourceType: ResourceType;
  propertyDefaultType: PropertyType;
  propertyName: string | string[];
  propertyObjectKey?: string | string[];
  propertyType: PropertyType;
  joinPropertyType?: PropertyType;
  unit?: TimeUnit | SpecialTimeUnit;
  customBucket?: Bucket;
  customGroups?: StringBucket;
  isHidden?: boolean;
  cohorts?: CohortDefinition[];

  /**
   * Segmentation can support filtering for the ProjectFeatureFlag.SingleSegment
   * feature flag, in that mode, we populate filter fields to
   * represent a segment + filter based on UI state
   *
   * DEPRECATED: SingleSegment / Value Comparison project seems to have been deprecated (https://github.com/mixpanel/analytics/pull/47955)
   */
  filterValue?: FilterValue;
  filterOperator?: FilterOperator;
  filterDateUnit?: TimeUnit | SpecialTimeUnit;
  listItemFilters?: PropertyFilter[];
  filterJoinType?: Type;

  /** For list properties ([tech spec](https://www.notion.so/mxpnl/Breakdown-QApi-Implementation-d35e842315024db78e10f72c179878b2)) */
  listItemGroup?: Segmentation;
  listInclusion?: ListInclusion;
  listQuantifier?: ListQuantifier;

  /** For TTC breakdowns */
  startStepNumber?: number;
  endStepNumber?: number;

  /** Defines a computed behavioral property based on each user's event stream */
  behavior?: Behavior;

  /**
   * Temp value for slowly changing dimensions POC
   */
  scdEvent?: string;
  /**
   * TODO: deprecate this after we migrate all profile history users to SCD
   */
  profileHistoryOptions?: ProfileHistoryOptions;
  /**
   * SCD options, similar to profileHistoryOptions
   */
  scdOptions?: {
    type?:
      | `any` // e.g. any user on free plan from Jan -> Feb
      // latest is the default. it maps to text "At the time of event"
      | `latest` // e.g. unique # of user on free plan on Feb 1st, Feb 2nd etc on a line chart
      | `fixed`; // e.g. unique # of user on free plan on Feb 1st
    dateRange?: DateRangeAttributes;
  };

  experiments?: {
    /** If true, QA users will be excluded from queries. */
    excludeQA: boolean;
  };

  /** used for borrowed properties, where this field indicates the event that the property is borrowed from */
  borrowEvent?: Event;

  /** Indicates a filter is assigned from an external source like a dashboard filter override */
  isInherited?: boolean;
};

export type BehavioralSegmentation = Segmentation & Required<Pick<Segmentation, `behavior`>>;

/** Specifies field members that define a given property selection on a filter or segment */
export const SegmentationPropertyFields = new Set<keyof Segmentation>([
  `tempDataGroupId`,
  `behavior`,
  `customPropertyId`,
  `customProperty`,
  `joinPropertyType`,
  `propertyObjectKey`,
  `propertyName`,
  `dataGroupId`,
  `scdEvent`,
  `scdOptions`,
  `cohorts`,
]);

/**
 * References Insights API resource types
 *
 * @deprecated legacy Insights only way of defining resource types
 */
export enum CustomPropertyResourceType {
  Events = `events`,
  People = `people`,
}

/**
 * Defines a computed property
 */
export type CustomProperty = {
  // field exists for saved custom properties, but not inline custom properties
  customPropertyId?: number;
  /** This is the DisplayName of the Custom Property. It is not unique. Do not use it as an identifier, like we do for regular PropertyDefinitions*/
  name: string;
  description: string;
  exampleValue?: string;
  resourceType?: CustomPropertyResourceType;
  displayFormula?: string;
  displayOptions?: PropertyDisplayOptions;
  composedProperties?: Record<string, TODO>;
  created?: string;
  modified?: string;

  /** @deprecated behaviors should be defined as a top level field on Segmentation */
  behavior?: Behavior;

  mappedDataGroupId?: string | null;
  // ReferencedBy is generated on the backend and can be utilized
  // to avoid cyclical references
  referencedDirectlyBy?: number[];
  referencedBy?: number[];
  referencedRawEventProperties?: string[];
  /**
   * Is this custom property a borrowed property or does it contain a nested custom property that's a
   * borrowed property
   * NOTE: This field is only specified for saved custom properties (where it's computed dynamically on the backend).
   * It's not specified for inline custom properties, since persisting could cause it to be inaccurate if the
   * definitions of nested custom properties are updated. To check if borrowed property is nested for both saved
   * and inline custom properties, use the function isBorrowedPropertyReferenced.
   * */
  referencesBorrowedProperty?: boolean;

  propertyType?: PropertyType;
  joinPropertyType?: PropertyType;
  scdEvent?: string;
  user?: number; // TODO @evnp - eliminate this confusing attr
  userId?: number;
  userName?: string;
  userEmail?: string;
  userInfo?: TODO;

  // customPropertiesStore holds the assumption that key (customPropertyId) of a
  // customProperty never changes, but this is no longer true now that we give
  // an inline custom properties a session only ID, from which the user can
  // convert to a real customPropertyId by 'save permanently'. We therefore need
  // a way to communicate to listeners of the store that the old Id is invalid
  // and here is the new Id
  newCustomPropertyId?: number;

  // permissions
  can_view?: boolean;
  canUpdateBasic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;

  // sharing-disabled (visibility controls in create-entity-modal)
  isVisible?: boolean;
  isLocked?: boolean;
  canUpdateLocked?: boolean;
  canUpdateVisible?: boolean;
  canUpdateRestricted?: boolean;
  canUpdateVisibility?: boolean;

  // Pre-Populated Custom Properties
  pre_populated_entity_id?: string;
};
